import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatStepperModule } from '@angular/material/stepper';
import { Router } from '@angular/router';

import {
  LanePosition,
  LanePositionLabel,
  RoundFormat,
  RoundFormatLabel,
  SessionMode,
  SessionModeLabel
} from '@core/models/brijjsports.enums';
import { LaneAssignment } from '@core/models/brijjsports.models';
import { maxPossible, totalArrows } from '@core/utils/scoring.util';

import { ArcherPickRowComponent } from '../components/archer-pick-row/archer-pick-row.component';
import { ArcherPick } from '../models/coach-scoring-setup.state';
import { CoachScoringSetupService } from '../services/coach-scoring-setup.service';

interface RoundFormGroup {
  format: FormControl<RoundFormat>;
  endsCount: FormControl<number>;
  arrowsPerEnd: FormControl<number>;
}

@Component({
  selector: 'app-coach-scoring-setup-page',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatCheckboxModule,
    MatDividerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatStepperModule,
    ArcherPickRowComponent
  ],
  templateUrl: './coach-scoring-setup-page.component.html',
  styleUrl: './coach-scoring-setup-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CoachScoringSetupPageComponent {
  protected readonly setup = inject(CoachScoringSetupService);
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);

  protected readonly modeOptions = [
    { value: SessionMode.ByArcher, label: SessionModeLabel[SessionMode.ByArcher] },
    { value: SessionMode.ByLane, label: SessionModeLabel[SessionMode.ByLane] },
    { value: SessionMode.CustomGroups, label: SessionModeLabel[SessionMode.CustomGroups] }
  ];

  protected readonly roundFormatOptions = [
    { value: RoundFormat.Indoor18m, label: RoundFormatLabel[RoundFormat.Indoor18m] },
    { value: RoundFormat.Vegas, label: RoundFormatLabel[RoundFormat.Vegas] },
    { value: RoundFormat.Joad, label: RoundFormatLabel[RoundFormat.Joad] },
    { value: RoundFormat.Wa1440, label: RoundFormatLabel[RoundFormat.Wa1440] },
    { value: RoundFormat.OlympicMatchPlay, label: RoundFormatLabel[RoundFormat.OlympicMatchPlay] },
    { value: RoundFormat.Custom, label: RoundFormatLabel[RoundFormat.Custom] }
  ];

  protected readonly lanePositions = [LanePosition.A, LanePosition.B, LanePosition.C];
  protected readonly lanePositionLabel = LanePositionLabel;
  protected readonly byLaneMode = SessionMode.ByLane;

  protected readonly basicsForm: FormGroup<{
    name: FormControl<string>;
    indoor: FormControl<boolean>;
    mode: FormControl<SessionMode>;
    compoundInner10: FormControl<boolean>;
  }> = this.fb.nonNullable.group({
    name: this.fb.nonNullable.control(`Session ${new Date().toLocaleDateString()}`, [
      Validators.required,
      Validators.maxLength(80)
    ]),
    indoor: this.fb.nonNullable.control(true),
    mode: this.fb.nonNullable.control<SessionMode>(SessionMode.ByArcher),
    compoundInner10: this.fb.nonNullable.control(false)
  });

  protected readonly roundsForm = this.fb.nonNullable.group({
    rounds: this.fb.nonNullable.array<FormGroup<RoundFormGroup>>([this.makeRoundGroup()])
  });

  protected readonly archerPicks = signal<ArcherPick[]>([]);

  protected readonly availableArchers = computed(() => this.setup.availableArchers());

  protected readonly previewArrows = computed(() => totalArrows(this.snapshotRounds()));
  protected readonly previewMax = computed(() =>
    maxPossible(this.snapshotRounds(), this.basicsForm.controls.compoundInner10.value)
  );

  protected get rounds() {
    return this.roundsForm.controls.rounds;
  }

  protected addRound(): void {
    if (this.rounds.length >= 6) return;
    this.rounds.push(this.makeRoundGroup());
  }

  protected removeRound(i: number): void {
    if (this.rounds.length <= 1) return;
    this.rounds.removeAt(i);
  }

  protected toggleArcher(athleteId: string): void {
    this.archerPicks.update(list => {
      const exists = list.find(p => p.athleteId === athleteId);
      if (exists) return list.filter(p => p.athleteId !== athleteId);
      return [...list, { athleteId, laneNumber: list.length + 1, position: LanePosition.A }];
    });
  }

  protected isPicked(athleteId: string): boolean {
    return this.archerPicks().some(p => p.athleteId === athleteId);
  }

  protected updateLane(athleteId: string, laneNumber: number): void {
    this.archerPicks.update(list => list.map(p => (p.athleteId === athleteId ? { ...p, laneNumber } : p)));
  }

  protected updatePosition(athleteId: string, position: LanePosition): void {
    this.archerPicks.update(list => list.map(p => (p.athleteId === athleteId ? { ...p, position } : p)));
  }

  protected laneFor(athleteId: string): number {
    return this.archerPicks().find(p => p.athleteId === athleteId)?.laneNumber ?? 1;
  }

  protected positionFor(athleteId: string): LanePosition {
    return this.archerPicks().find(p => p.athleteId === athleteId)?.position ?? LanePosition.A;
  }

  protected onLaneInput(athleteId: string, ev: Event): void {
    const value = Number((ev.target as HTMLInputElement).value);
    if (Number.isFinite(value) && value > 0) this.updateLane(athleteId, value);
  }

  protected canStart = computed(() => this.archerPicks().length > 0 && this.basicsForm.valid && this.roundsForm.valid);

  protected async start(): Promise<void> {
    if (!this.canStart()) return;
    const picks = this.archerPicks();
    const isLaneMode = this.basicsForm.controls.mode.value === SessionMode.ByLane;
    const lanes: LaneAssignment[] = isLaneMode
      ? picks.map(p => ({ laneNumber: p.laneNumber, position: p.position, athleteId: p.athleteId }))
      : [];
    const session = await this.setup.create({
      name: this.basicsForm.controls.name.value,
      indoor: this.basicsForm.controls.indoor.value,
      mode: this.basicsForm.controls.mode.value,
      rounds: this.snapshotRounds(),
      participantIds: picks.map(p => p.athleteId),
      compoundInner10: this.basicsForm.controls.compoundInner10.value,
      lanes
    });
    this.router.navigate(['/coach/scoring/live', session.id]);
  }

  protected cancel(): void {
    this.router.navigate(['/coach/scoring']);
  }

  private snapshotRounds() {
    return this.rounds.controls.map(g => ({
      format: g.controls.format.value,
      endsCount: g.controls.endsCount.value,
      arrowsPerEnd: g.controls.arrowsPerEnd.value
    }));
  }

  private makeRoundGroup(): FormGroup<RoundFormGroup> {
    return this.fb.nonNullable.group<RoundFormGroup>({
      format: this.fb.nonNullable.control<RoundFormat>(RoundFormat.Indoor18m),
      endsCount: this.fb.nonNullable.control(6, [Validators.required, Validators.min(4), Validators.max(30)]),
      arrowsPerEnd: this.fb.nonNullable.control(6, [Validators.required, Validators.min(1), Validators.max(6)])
    });
  }
}
